import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import Table from '@components/Table';
import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  Button,
  Dropdown,
  Input,
  Select,
  Image,
  message,
  DatePicker,
} from 'antd';
import {
  PlusCircleOutlined,
  EyeOutlined,
  LinkOutlined,
  FileImageOutlined,
} from '@ant-design/icons';
import PadlockStatusColumn from '@components/PadlockStatusColumn';
import { dateFormat } from '@utils/formatters';
import api from '@services/api';
import useSwr from '@hooks/useSwr';
import useSearchParams from '@hooks/useSearchParams';
import AdminActions from '@components/AdminActions';
import { BannerData } from '@services/types/General/banners';
import SessionContext from '@providers/Session/SessionContext';
import moment from 'moment';
import { ProductsConstants } from '@utils/applicationsConstants';
import BannerModal from './BannerModal';
import BannerDisplayTimeCol from './BannerDisplayTimeCol';
import BannerRecipientsCol from './BannerRecipientsCol';

const { Search } = Input;

const Banners = () => {
  const { t } = useTranslation('banners');
  const { isRoot } = useContext(SessionContext);

  const [modalVisible, setModalVisible] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState<string>();
  const [bannerToEdit, setBannerEdit] = useState<BannerData>();
  const [isEdition, setIsEdition] = useState(false);
  const [viewBannerImg, setViewBannerImg] = useState<{
    visible: boolean;
    imgSrc?: string;
  }>();

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    extraParams: [
      'description',
      'active',
      'status',
      'customerType',
      'product',
      'startDate',
      'expirationDate',
    ],
  });

  const {
    description,
    active,
    status,
    customerType,
    product,
    startDate,
    expirationDate,
    page,
    linesPerPage,
  } = useMemo(() => searchParams, [searchParams]);

  useEffect(() => {
    setDescriptionValue(description);
  }, [description]);

  const formatDate = useCallback(
    (value: string | undefined, startOrEnd?: string) => {
      const format = `YYYY-MM-DDT${
        startOrEnd === 'end' ? '23:59:59' : 'HH:mm:ss'
      }`;
      return moment(value)?.format(format);
    },
    [],
  );

  const { data, mutate } = useSwr(
    '/service-proxy/banner',
    {
      params: {
        description,
        active,
        status,
        startDate: startDate && formatDate(startDate),
        expirationDate: expirationDate && formatDate(expirationDate, 'end'),
        displayIntentEnum: customerType,
        displayProductEnum: product,
        page: page ? Number(page) - 1 : 0,
        linesPerPage: linesPerPage ?? 10,
      },
    },
    {
      revalidateOnFocus: true,
      shouldRetryOnError: true,
    },
  );

  const deleteBanner = useCallback(
    (bannerId: number) => api.delete(`/service-proxy/banner/${bannerId}`),
    [],
  );

  const changeStatus = useCallback(
    async (bannerId: number, value: boolean) => {
      const actionType = value ? 'enabling' : 'disabling';
      try {
        await api.put('/service-proxy/banner/active', null, {
          params: {
            active: value,
            bannerId,
          },
        });
        message.success(t(`msgs.success-when-${actionType}`));
      } catch {
        message.error(t(`msgs.error-when-${actionType}`));
      }
    },
    [t],
  );

  const mutateStatus = useCallback(
    (bannerId: number, value?: boolean) => {
      mutate({
        ...data,
        content: data?.content?.map((banner: BannerData) =>
          banner.id === bannerId ? { ...banner, active: value } : banner,
        ),
      });
    },
    [data, mutate],
  );

  const mutateCreate = useCallback(
    (newBanner: BannerData) => {
      if (data) {
        mutate({
          ...data,
          content: [newBanner, ...data.content],
        });
      }
    },
    [data, mutate],
  );

  const mutateUpdate = useCallback(
    (updatedBanner: BannerData) => {
      if (data) {
        mutate({
          ...data,
          content: data?.content?.map((banner: BannerData) =>
            banner.id === updatedBanner.id ? updatedBanner : banner,
          ),
        });
      }
    },
    [data, mutate],
  );

  const mutateDelete = useCallback(
    (bannerId: number) => {
      mutate({
        ...data,
        content: data?.content?.filter(
          (banner: BannerData) => banner.id !== bannerId,
        ),
      });
    },
    [data, mutate],
  );

  const handleDelete = useCallback(
    async (payload: any) => {
      try {
        await deleteBanner(payload);
        mutateDelete(payload);
        message.success(t('msgs.success-when-deleting'));
      } catch {
        message.error(t('msgs.error-when-deleting'));
      }
    },
    [deleteBanner, mutateDelete, t],
  );

  const viewItems = useCallback(
    (fileName: string, fileDetailName: string, link: string) => {
      const hasLink = !!(link && link !== '');

      const handleViewLink = (
        name: string,
        buttonText: string,
        hasLinkToView?: boolean,
        linkToView?: string,
      ) => (
        <Button
          type="link"
          style={{ background: 'transparent' }}
          onClick={() => {
            if (hasLinkToView) {
              window.open(linkToView, '_blank', 'noreferrer');
            } else {
              const { hostname } = window.location;
              const isLocalhost = hostname === 'localhost';

              const imgSrc = `${
                isLocalhost ? process.env.REACT_APP_ROOT_URL : ''
              }/banners/${name}`;

              setViewBannerImg({
                visible: true,
                imgSrc,
              });
            }
          }}
          icon={hasLinkToView ? <LinkOutlined /> : <FileImageOutlined />}
        >
          {buttonText}
        </Button>
      );

      return [
        {
          key: 'main',
          label: handleViewLink(fileName, t('buttons.main')),
        },
        {
          key: hasLink ? 'link' : 'details',
          label: handleViewLink(
            fileDetailName,
            t(`buttons.${hasLink ? 'link' : 'details'}`),
            hasLink,
            link,
          ),
        },
      ];
    },
    [t],
  );

  const columns = useMemo(
    () => [
      { title: t('columns.description'), dataIndex: 'description' },
      {
        title: t('columns.displayTime.title'),
        dataIndex: 'displayTime',
        render: (_: any, record: BannerData) => (
          <BannerDisplayTimeCol
            startDate={record?.startDate}
            expirationDate={record?.expirationDate}
            bannerStatus={record?.bannerStatusEnum}
          />
        ),
      },
      {
        title: t('columns.recipients.title'),
        dataIndex: 'recipients',
        render: (_: any, record: BannerData) => (
          <BannerRecipientsCol
            type={record?.bannerDisplayIntentEnum}
            product={record?.bannerDisplayProductEnum ?? 'ALL'}
            customers={record?.customers}
          />
        ),
      },
      {
        title: t('columns.status.title'),
        dataIndex: 'active',
        render: (value: boolean, record: BannerData) => (
          <PadlockStatusColumn
            isOpen={value}
            openText={t('columns.status.able')}
            closedText={t('columns.status.disabled')}
            openActionTooltip={t('columns.status.enable')}
            closedActionTooltip={t('columns.status.disable')}
            onAction={() => {
              changeStatus(record.id, !value).then(() =>
                mutateStatus(record?.id, !value),
              );
            }}
            disabled={record?.bannerStatusEnum === 'EXPIRED'}
          />
        ),
      },
      {
        title: t('columns.createdAt'),
        dataIndex: 'createdAt',
        render: dateFormat,
      },
      {
        title: t('columns.createdBy'),
        dataIndex: 'createdBy',
      },
      {
        title: t('columns.actions'),
        dataIndex: 'actions',
        align: 'center' as const,
        render: (_: any, record: BannerData) => (
          <AdminActions
            alertDelete={t('msgs.delete')}
            entityToExclude={record.description}
            extraActions={
              <Tooltip title={t('buttons.view')} style={{ background: 'red' }}>
                {record?.fileDetailName ||
                (record?.link && record?.link !== '') ? (
                  <Dropdown
                    menu={{
                      items: viewItems(
                        record?.fileName,
                        record?.fileDetailName,
                        record?.link,
                      ),
                    }}
                  >
                    <Button style={{ padding: '0 8px' }}>
                      <EyeOutlined />
                    </Button>
                  </Dropdown>
                ) : (
                  <Button
                    style={{ padding: '0 8px' }}
                    onClick={() => {
                      setViewBannerImg({
                        visible: true,
                        imgSrc: `/banners/${record?.fileName}`,
                      });
                    }}
                  >
                    <EyeOutlined />
                  </Button>
                )}
              </Tooltip>
            }
            onDelete={() => handleDelete(record.id)}
            onEdit={() => {
              setBannerEdit(record);
              setIsEdition(true);
              setModalVisible(true);
            }}
          />
        ),
      },
    ],
    [changeStatus, handleDelete, viewItems, mutateStatus, t],
  );

  const handleOnCloseModal = useCallback(() => {
    setModalVisible(false);
    setBannerEdit(undefined);
    setIsEdition(false);
  }, []);

  const customerTypesOptions = useMemo(
    () =>
      ['BROKER', 'CUSTOMER'].map(type => ({
        label: t(`columns.recipients.types.${type}`),
        value: type,
      })),
    [t],
  );

  const productsOptions = useMemo(
    () =>
      Object.values(ProductsConstants).map(productType => ({
        label: t(`main:apps.${productType}`),
        value: productType?.toUpperCase(),
      })),
    [t],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        label: t('columns.description'),
        element: (
          <Search
            onSearch={value => setSearchParams({ description: value })}
            onChange={({ target: { value } }) => setDescriptionValue(value)}
            value={descriptionValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('columns.status.title'),
        element: (
          <Select
            style={{ width: '100%' }}
            onChange={value => setSearchParams({ active: value })}
            options={[
              {
                label: t('columns.status.able'),
                value: 'true',
              },
              {
                label: t('columns.status.disabled'),
                value: 'false',
              },
            ]}
            value={active}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('columns.status.viewing-status'),
        element: (
          <Select
            style={{ width: '100%' }}
            onChange={value => setSearchParams({ status: value })}
            options={[
              {
                label: t('columns.displayTime.status.ACTIVE'),
                value: 'ACTIVE',
              },
              {
                label: t('columns.displayTime.status.WAITING'),
                value: 'WAITING',
              },
              {
                label: t('columns.displayTime.status.EXPIRED'),
                value: 'EXPIRED',
              },
            ]}
            value={status}
            allowClear
          />
        ),
      },
      {
        visible: isRoot,
        label: t('columns.customerType'),
        element: (
          <Select
            style={{ width: '100%' }}
            onChange={value => setSearchParams({ customerType: value })}
            options={customerTypesOptions}
            value={customerType}
            allowClear
          />
        ),
      },
      {
        visible: isRoot,
        label: t('columns.productType'),
        element: (
          <Select
            style={{ width: '100%' }}
            onChange={value => setSearchParams({ product: value })}
            options={productsOptions}
            value={product}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('modal.labels.start-date'),
        element: (
          <DatePicker
            placeholder=""
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            value={startDate && moment(startDate)}
            onChange={value => {
              setSearchParams({
                startDate: value?.format('YYYYMMDD'),
              });
            }}
          />
        ),
      },
      {
        visible: true,
        label: t('modal.labels.expiration-date'),
        element: (
          <DatePicker
            placeholder=""
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            value={expirationDate && moment(expirationDate)}
            onChange={value => {
              setSearchParams({
                expirationDate: value?.format('YYYYMMDD'),
              });
            }}
            disabledDate={current => {
              return current && current < (startDate && moment(startDate));
            }}
          />
        ),
      },
    ],
    [
      t,
      descriptionValue,
      active,
      status,
      isRoot,
      customerTypesOptions,
      customerType,
      productsOptions,
      product,
      startDate,
      expirationDate,
      setSearchParams,
    ],
  );

  return (
    <div>
      <BannerModal
        visible={modalVisible}
        onClose={handleOnCloseModal}
        mutate={(banner: BannerData) => {
          isEdition ? mutateUpdate(banner) : mutateCreate(banner);
        }}
        isEdition={isEdition}
        bannerToEdit={bannerToEdit}
      />
      {viewBannerImg?.visible && (
        <Image
          src={viewBannerImg?.imgSrc}
          height={0}
          preview={{
            visible: viewBannerImg?.visible,
            onVisibleChange: visible =>
              setViewBannerImg({ visible, imgSrc: undefined }),
          }}
        />
      )}
      <Table
        columns={columns}
        data={data?.content}
        loading={!data}
        title={t('title')}
        tableKey="banners"
        extraActions={
          <Tooltip placement="top" title={t('modal.title-new')}>
            <Button
              icon={<PlusCircleOutlined />}
              onClick={() => setModalVisible(true)}
            />
          </Tooltip>
        }
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
        onTableChange={setSearchParams}
        pagination={{
          page,
          linesPerPage: linesPerPage ?? 10,
          total: data?.totalElements,
        }}
      />
    </div>
  );
};

export default Banners;
